/**
 * Каталог сфер для интерфейса.
 *
 * Читается один раз на всё приложение и раздаётся через контекст (провайдер —
 * в `ui/SphereCatalogProvider`). Иначе подписка висела бы на каждой карточке
 * задачи и цели — а их на экране десятки, и все спрашивают у каталога одно и
 * то же.
 *
 * Значение по умолчанию — встроенные восемь. Компонент, отрисованный без
 * провайдера (в тестах, в изолированном рендере), из-за этого не падает и
 * ведёт себя ровно как до появления своих сфер.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { createContext, useContext } from 'react';

import type { SphereId } from '@/shared/config/spheres';
import { db } from '@/shared/db/database';
import { alive } from '@/shared/db/repository';
import type { CustomSphere } from '@/shared/db/schema';
import { getCurrentUserId } from '@/shared/db/session-context';

import { DEFAULT_CATALOG, type SphereCatalog, type SphereInfo } from './catalog';

export const SphereCatalogContext = createContext<SphereCatalog>(DEFAULT_CATALOG);

/** Свои сферы текущего пользователя — их единицы, читаются целиком. */
export function useCustomSpheres(): CustomSphere[] | undefined {
  return useLiveQuery(async () => {
    const userId = getCurrentUserId();
    if (!userId) return [];

    const rows = await db.spheres.where('userId').equals(userId).toArray();
    return alive(rows).sort((a, b) => a.position - b.position);
  }, []);
}

/** Весь каталог: список для выбора и поиск по ключу. */
export function useSphereCatalog(): SphereCatalog {
  return useContext(SphereCatalogContext);
}

/** Подписи и цвет одной сферы — самый частый запрос карточек. */
export function useSphere(id: SphereId): SphereInfo {
  return useSphereCatalog().get(id);
}
